/**
 * Resolution of a left-region panel's state from the three things that decide
 * it. See `docs/hypedrive/17-shell-rearchitecture.md` §2.
 *
 * Kept as a pure function rather than a getter so the whole resolution table
 * can be asserted directly, including the combinations that were unreachable
 * before it existed. `store/shell.js` is the only caller; components read the
 * store, never this.
 *
 * The three inputs are unchanged in meaning from the code this replaces --
 * `app.vue`'s `col1Narrow` / `col1ForcedByViews` / `col1CollapsedPreference`
 * and `Table.vue`'s three equivalents. What changes is that they are combined
 * once, here, instead of by two components that cannot see each other. That
 * duplication is what let `Table.vue` hold two formulas which contradict each
 * other: `overlay = narrow && !collapsed` next to `collapsed = narrow || …`,
 * which makes `overlay` unreachable for every input.
 */

// The four states from `05-sidebar-system.md` §3.3. An enum rather than the
// three booleans it replaces: three booleans describe eight combinations, of
// which four are meaningless, and it was precisely an impossible combination
// that the old code was built out of.
export const PANEL_EXPANDED = 'expanded'
export const PANEL_RAIL = 'rail'
export const PANEL_OVERLAY = 'overlay'
export const PANEL_HIDDEN = 'hidden'

// Which side of the breakpoints the viewport is on. Named rather than passed as
// two booleans so an impossible pair (`narrow && !medium`) cannot be expressed.
export const BAND_COMPACT = 'compact' // < sm
export const BAND_MEDIUM = 'medium' // [sm, md)
export const BAND_WIDE = 'wide' // >= md

/**
 * @param {string} band One of `BAND_*`, from the viewport width.
 * @param {Object} options
 * @param {boolean} options.collapsedPreference What the user last chose. Only
 *   consulted in the wide band, where it is the only thing left to decide.
 * @param {boolean} options.forcedByBudget True when the *other* left panel
 *   currently holds the expansion budget (Model C, `store/shell.js`).
 * @param {boolean} options.overlayOpen Whether the user has opened this panel
 *   as an overlay. Only meaningful in the compact band.
 * @return {string} One of `PANEL_*`.
 */
export function resolvePanelState(
  band,
  {
    collapsedPreference = false,
    forcedByBudget = false,
    overlayOpen = false,
  } = {}
) {
  // 1. Viewport band outranks everything. Below `sm` there is no room for a
  //    rail beside the content, so the panel is either absent or floating over
  //    it -- a preference cannot conjure width that is not there.
  if (band === BAND_COMPACT) {
    return overlayOpen ? PANEL_OVERLAY : PANEL_HIDDEN
  }

  // In `[sm, md)` the rail is the default, and opening it floats an overlay
  // over the content rather than pushing it. Pushing would mean 240px of chrome
  // against an 800px viewport; shadcn/ui makes exactly this trade, rendering
  // its sidebar as a sheet below the desktop breakpoint instead of a column.
  //
  // What changed is that the panel is now *openable* here at all. It used to
  // return `PANEL_RAIL` unconditionally, which -- combined with a toggle that
  // hid itself in this band -- left no way to reach either panel between 768px
  // and 1200px.
  //
  // The preference is still neither consulted nor overwritten, so widening the
  // window keeps giving the user back whatever they last asked for.
  if (band === BAND_MEDIUM) {
    return overlayOpen ? PANEL_OVERLAY : PANEL_RAIL
  }

  // 2. The navigation budget. At `>= md` both panels fit individually but not
  //    together, so only the holder may expand.
  if (forcedByBudget) {
    return PANEL_RAIL
  }

  // 3. Only now does what the user chose decide anything.
  return collapsedPreference ? PANEL_RAIL : PANEL_EXPANDED
}

/**
 * The band for a viewport width. Kept beside the resolver so the two cannot
 * drift; the breakpoints themselves stay in `utils/breakpoints.js`, which is
 * mirrored from SCSS under its own guard test.
 *
 * The comparisons match the `below()` mixin's exclusive upper bound, so a
 * viewport exactly at a breakpoint is in the *higher* band -- the same
 * convention `layout.scss`'s media queries use. A disagreement here would put
 * JS and CSS on opposite sides of a boundary, which is how an expanded sidebar
 * ended up rendering inside a 52px column.
 */
export function bandForWidth(width, { sm, md }) {
  if (width < sm) {
    return BAND_COMPACT
  }
  return width < md ? BAND_MEDIUM : BAND_WIDE
}

/**
 * Whether a state pushes the content region aside rather than floating over it.
 * `overlay` and `hidden` do not, which is the whole point of the compact band:
 * the content keeps the full width.
 */
export function panelOccupiesColumn(panelState) {
  return panelState === PANEL_EXPANDED || panelState === PANEL_RAIL
}

/**
 * Whether a state draws the icon-only rail presentation rather than the full
 * panel with labels.
 *
 * `rail` is the only one. `expanded` obviously is not; `overlay` floats over
 * the content instead of sharing a column with it, so there is no width left
 * to save by narrowing it -- §2.2 gives it "user width, floating", the same
 * width as `expanded`. Before this had its own name, both `app.vue` and
 * `Table.vue` read "not expanded" as the icon-only condition, which also
 * caught `overlay`: the one state meant to show the full sidebar on a phone
 * measured live at 52px, icons only, the same as the rail it is not.
 */
export function panelIsIconOnly(panelState) {
  return panelState === PANEL_RAIL
}

/**
 * Whether the collapse toggle is a real control in this state.
 *
 * In the medium band the rail is forced by the viewport, so a toggle there can
 * do nothing. It used to render anyway, with `aria-expanded` flipping on a
 * click that changed nothing -- announcing a state change that did not happen,
 * which is worse than having no control at all.
 */
export function panelToggleIsActionable(panelState, band) {
  // Always. A trigger that disappears with the panel it controls is the one
  // failure every responsive-shell reference warns against: shadcn/ui puts its
  // `SidebarTrigger` in the *content* area precisely "so it remains accessible
  // on all screen sizes", and the WAI guidance on collapsed sections is that
  // focus should land on "the element that expands" it -- which requires that
  // element to exist.
  //
  // This used to return false in `[sm, md)`, on the reasoning that the viewport
  // forced the rail so the toggle "could only lie about what it does". That
  // held only while the medium band forced the rail unconditionally. It no
  // longer does: `resolvePanelState` now honours the user's preference there,
  // so the toggle acts, and removing it stranded anyone between those two
  // widths with no way to open either panel.
  return true
}
